import * as React from "react";
import { useState, useContext } from "react";
import chroma from "chroma-js";
import styled, { css } from "styled-components";
import SearchPagePreferencesContext from "views/components/contexts/SearchPagePreferencesContext";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
import zIndices from "theme/z-indices";
import SidebarNavigation from "./SidebarNavigation";
import ContentColumn from "./ContentColumn";
import sidebarSections from "./sidebarSections";
import sidebarActions from "./sidebarActions";
const Container = styled.div`
  display: flex;
  height: 100%;
  width: min-content;
`;
const ContentOverlay = styled.div(
  ({ theme }) => css`
    position: fixed;
    inset: 0 0 0 50px;
    background: ${chroma(theme.colors.brand.tertiary).alpha(0.25).css()};
    z-index: ${zIndices.sidebarOverlay};
  `
);
const _toggleSidebar = (initialSectionKey, activeSectionKey, setActiveSectionKey) => {
  if (activeSectionKey) {
    setActiveSectionKey(null);
    return;
  }
  setActiveSectionKey(initialSectionKey);
};
const _selectSidebarSection = (sectionKey, activeSectionKey, setActiveSectionKey, toggleSidebar) => {
  if (sectionKey === activeSectionKey) {
    toggleSidebar();
    return;
  }
  setActiveSectionKey(sectionKey);
};
const Sidebar = ({
  searchPreferencesLayout = void 0,
  results = void 0,
  children = void 0,
  title,
  sections = sidebarSections,
  actions = sidebarActions,
  forceSideBarPinned = false,
  enableSidebarPinning = true,
  contentColumnWidth = 275
}) => {
  const sendTelemetry = useSendTelemetry();
  const sidebarIsPinned = (searchPreferencesLayout == null ? void 0 : searchPreferencesLayout.config.sidebar.isPinned) || forceSideBarPinned;
  const initialSectionKey = sections[0].key;
  const [activeSectionKey, setActiveSectionKey] = useState(
    sidebarIsPinned ? initialSectionKey : null
  );
  const activeSection = sections.find((section) => section.key === activeSectionKey);
  const toggleSidebar = () => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.SEARCH_SIDEBAR_TOGGLE, {
      app_action_value: "search_sidebar",
      initialSectionKey,
      activeSectionKey
    });
    _toggleSidebar(initialSectionKey, activeSectionKey, setActiveSectionKey);
  };
  const selectSidebarSection = (sectionKey) => _selectSidebarSection(sectionKey, activeSectionKey, setActiveSectionKey, toggleSidebar);
  const SectionContent = activeSection == null ? void 0 : activeSection.content;
  return /* @__PURE__ */ React.createElement(Container, null, /* @__PURE__ */ React.createElement(
    SidebarNavigation,
    {
      activeSection,
      selectSidebarSection,
      sections,
      sidebarIsPinned,
      actions
    }
  ), activeSection && !!SectionContent && /* @__PURE__ */ React.createElement(
    ContentColumn,
    {
      closeSidebar: toggleSidebar,
      title,
      enableSidebarPinning,
      searchPreferencesLayout,
      sectionTitle: activeSection.title,
      forceSideBarPinned,
      width: contentColumnWidth
    },
    /* @__PURE__ */ React.createElement(
      SectionContent,
      {
        results,
        sidebarChildren: children,
        sidebarIsPinned,
        toggleSidebar
      }
    )
  ), activeSection && !sidebarIsPinned && /* @__PURE__ */ React.createElement(ContentOverlay, { onClick: toggleSidebar }));
};
const SidebarWithContext = ({ children = void 0, ...props }) => {
  const searchPreferencesLayout = useContext(SearchPagePreferencesContext);
  return /* @__PURE__ */ React.createElement(Sidebar, { ...props, searchPreferencesLayout }, children);
};
export default SidebarWithContext;
